// «The global search field feeds the booking panel» (board
// booking/new--existing-customer caption: typing the number in the top-bar
// search opens the panel with the matching customer).
//
// The top bar is shell-owned and navigates to /customers?q= on submit. While
// the booking panel is open this hook listens to that field (found by its
// role — `form[role="search"]` inside the top bar — not by a class name) and:
//   · forwards every keystroke to the panel's customer step;
//   · swallows the submit so Enter stays in the panel instead of leaving it.
// SHARED-REQUESTS asks the shell for a proper hook (TopBar `onSearch` routed
// to the open panel); this is the module-local bridge until then.

import { useEffect, useRef } from 'react';

function topBarSearch(): { form: HTMLFormElement; input: HTMLInputElement } | null {
  const form = document.querySelector<HTMLFormElement>('[data-testid="top-bar"] form[role="search"]');
  const input = form?.querySelector<HTMLInputElement>('input') ?? null;
  return form && input ? { form, input } : null;
}

/** The top-bar search text right now ('' when the shell has none). */
export function currentGlobalQuery(): string {
  return topBarSearch()?.input.value.trim() ?? '';
}

export function useGlobalSearchFeed(enabled: boolean, onQuery: (q: string) => void): void {
  const cb = useRef(onQuery);
  cb.current = onQuery;
  useEffect(() => {
    if (!enabled) return;
    const found = topBarSearch();
    if (!found) return;
    const { form, input } = found;
    const onInput = () => cb.current(input.value);
    const onSubmit = (e: Event) => {
      // Native listener on the form itself runs before React's root
      // listener: stopping here keeps the shell from navigating away.
      e.preventDefault();
      e.stopPropagation();
      cb.current(input.value);
    };
    input.addEventListener('input', onInput);
    form.addEventListener('submit', onSubmit);
    return () => {
      input.removeEventListener('input', onInput);
      form.removeEventListener('submit', onSubmit);
    };
  }, [enabled]);
}
